<template>
  <div class="rs-filter">
        <!-- Titlen tegnes af indpakningen, som også bærer sammenklapningen. -->
    <div class="rs-filter__body">
      <!-- Slider track -->
      <div class="rs-track" ref="sliderTrack" @mousedown="onTrackMouseDown" @touchstart="onTrackTouchStart">
        <div class="rs-track__bar" :style="barStyle"></div>
        <div
          class="rs-track__handle"
          :style="minHandleStyle"
          tabindex="0"
          @mousedown.prevent="startDrag('min', $event)"
          @touchstart.prevent="startDrag('min', $event)"
          @keydown="onHandleKey('min', $event)"
          :aria-label="`${filter.translation_string_ident} min`"
        ></div>
        <div
          class="rs-track__handle"
          :style="maxHandleStyle"
          tabindex="0"
          @mousedown.prevent="startDrag('max', $event)"
          @touchstart.prevent="startDrag('max', $event)"
          @keydown="onHandleKey('max', $event)"
          :aria-label="`${filter.translation_string_ident} max`"
        ></div>
        <input type="hidden" :name="`${filter.id}_min`" :value="min">
        <input type="hidden" :name="`${filter.id}_max`" :value="max">
      </div>

      <!-- Min/Max labels -->
      <div class="rs-filter__range-labels">
        <span>{{ minValue }}</span>
        <span>{{ maxValue }}</span>
      </div>

      <!-- Min/Max inputs -->
      <div class="rs-filter__inputs">
        <div class="rs-filter__input-group">
          <label class="rs-filter__label">Min</label>
          <input type="number"
                 class="rs-filter__input"
                 :min="minValue"
                 :max="max"
                 :step="step"
                 v-model.number="min"
                 @blur="onMinInput"
          >
        </div>
        <div class="rs-filter__input-group">
          <label class="rs-filter__label">Max</label>
          <input type="number"
                 class="rs-filter__input"
                 :min="min"
                 :max="maxValue"
                 :step="step"
                 v-model.number="max"
                 @blur="onMaxInput"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { alleAktiveFiltre } from '@/Components/FilterManager.vue';

const props = defineProps<{ filter: any; urlParams: URLSearchParams, categoryName: string }>();
const emit = defineEmits(['filterUpdated']);

const minValue = computed(() => props.filter.values?.min ?? 0);
const maxValue = computed(() => props.filter.values?.max ?? 100);
const step = computed(() => props.filter.values?.step ?? 1);

const initialMin = computed(() => {
  const param = props.urlParams.get(`${props.filter.id}_min`);
  return param !== null ? Number(param) : (props.filter.selected_min ?? minValue.value);
});
const initialMax = computed(() => {
  const param = props.urlParams.get(`${props.filter.id}_max`);
  return param !== null ? Number(param) : (props.filter.selected_max ?? maxValue.value);
});

const min = ref(initialMin.value);
const max = ref(initialMax.value);

const sliderTrack = ref<HTMLElement|null>(null);
const dragging = ref<'min'|'max'|null>(null);

watch(() => [props.urlParams.get(`${props.filter.id}_min`), props.urlParams.get(`${props.filter.id}_max`)], ([newMin, newMax]) => {
  if (!dragging.value) {
    if (newMin !== null && newMin !== '') min.value = Number(newMin);
    if (newMax !== null && newMax !== '') max.value = Number(newMax);
  }
});

function emitUpdate() {
  if (min.value === minValue.value && max.value === maxValue.value) {
    /*
     * ALLE aktive filtre sendes med, ikke kun prisen.
     *
     * Prisfilteret findes i HVER kategori, og updateQueryParamsAndReload
     * sletter enhver parameter der ikke står i listen. En bevægelse på
     * prisskyderen ryddede derfor alt andet brugeren havde valgt.
     */
    emit('filterUpdated', alleAktiveFiltre(props.urlParams, props.filter.id, ''));
    return;
  }

  emit('filterUpdated', alleAktiveFiltre(props.urlParams, props.filter.id, `${min.value};${max.value}`));
}

function clamp(val: number, minV: number, maxV: number) {
  return Math.max(minV, Math.min(val, maxV));
}

function valueToPercent(val: number) {
  return ((val - minValue.value) / (maxValue.value - minValue.value)) * 100;
}

function percentToValue(percent: number) {
  const raw = minValue.value + (percent / 100) * (maxValue.value - minValue.value);
  return Math.round(raw / step.value) * step.value;
}

const minHandleStyle = computed(() => ({
  left: `calc(${clamp(valueToPercent(min.value), 0, 100)}% - 9px)`
}));
const maxHandleStyle = computed(() => ({
  left: `calc(${clamp(valueToPercent(max.value), 0, 100)}% - 9px)`
}));
const barStyle = computed(() => {
  const left = clamp(valueToPercent(min.value), 0, 100);
  const right = clamp(valueToPercent(max.value), 0, 100);
  return { left: `${left}%`, width: `${right - left}%` };
});

function onMinInput() {
  min.value = clamp(min.value, minValue.value, max.value - step.value);
  emitUpdate();
}
function onMaxInput() {
  max.value = clamp(max.value, min.value + step.value, maxValue.value);
  emitUpdate();
}

function startDrag(which: 'min'|'max', event: MouseEvent|TouchEvent) {
  dragging.value = which;
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('touchmove', onDrag, { passive: false });
  window.addEventListener('mouseup', stopDrag);
  window.addEventListener('touchend', stopDrag);
}
function stopDrag() {
  dragging.value = null;
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('touchmove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('touchend', stopDrag);
}
function onDrag(e: MouseEvent|TouchEvent) {
  if (!sliderTrack.value || !dragging.value) return;
  let clientX;
  if (e instanceof TouchEvent) {
    clientX = e.touches[0]?.clientX;
    if (clientX === undefined) return;
    e.preventDefault();
  } else {
    clientX = e.clientX;
  }
  const rect = sliderTrack.value.getBoundingClientRect();
  let percent = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  let newValue = percentToValue(percent);
  if (dragging.value === 'min') {
    min.value = clamp(newValue, minValue.value, max.value - step.value);
  } else {
    max.value = clamp(newValue, min.value + step.value, maxValue.value);
  }
  emitUpdate();
}
function onHandleKey(which: 'min'|'max', e: KeyboardEvent) {
  if (which === 'min') {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      min.value = clamp(min.value - step.value, minValue.value, max.value - step.value);
      emitUpdate();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      min.value = clamp(min.value + step.value, minValue.value, max.value - step.value);
      emitUpdate();
    }
  } else {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      max.value = clamp(max.value - step.value, min.value + step.value, maxValue.value);
      emitUpdate();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      max.value = clamp(max.value + step.value, min.value + step.value, maxValue.value);
      emitUpdate();
    }
  }
}
function onTrackMouseDown(e: MouseEvent) {
  if (!sliderTrack.value) return;
  const rect = sliderTrack.value.getBoundingClientRect();
  const percent = ((e.clientX - rect.left) / rect.width) * 100;
  const value = percentToValue(percent);
  if (Math.abs(value - min.value) < Math.abs(value - max.value)) {
    startDrag('min', e);
    min.value = clamp(value, minValue.value, max.value - step.value);
  } else {
    startDrag('max', e);
    max.value = clamp(value, min.value + step.value, maxValue.value);
  }
  emitUpdate();
}
function onTrackTouchStart(e: TouchEvent) {
  if (!sliderTrack.value) return;
  const clientX = e.touches[0]?.clientX;
  if (clientX === undefined) return;
  const rect = sliderTrack.value.getBoundingClientRect();
  const percent = ((clientX - rect.left) / rect.width) * 100;
  const value = percentToValue(percent);
  if (Math.abs(value - min.value) < Math.abs(value - max.value)) {
    startDrag('min', e);
    min.value = clamp(value, minValue.value, max.value - step.value);
  } else {
    startDrag('max', e);
    max.value = clamp(value, min.value + step.value, maxValue.value);
  }
  emitUpdate();
}
onMounted(() => {
  onBeforeUnmount(() => stopDrag());
});
</script>

<style scoped>
.rs-filter__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--pc-text-primary);
  margin-bottom: 0.5rem;
  text-transform: capitalize;
}

.rs-filter__body {
  padding: 0.5rem;
  background: var(--pc-bg-tertiary);
  border-radius: var(--pc-radius);
  border: 1px solid var(--pc-border);
}

/* ── Track ── */
.rs-track {
  position: relative;
  height: 2.25rem;
  margin-bottom: 0.25rem;
  user-select: none;
  background: var(--pc-bg-primary);
  border-radius: var(--pc-radius);
  border: 1px solid var(--pc-border);
  cursor: pointer;
}

.rs-track__bar {
  position: absolute;
  top: 50%;
  height: 6px;
  background: var(--pc-accent);
  border-radius: 3px;
  transform: translateY(-50%);
  z-index: 2;
}

.rs-track__handle {
  position: absolute;
  top: 50%;
  width: 18px;
  height: 18px;
  background: var(--pc-bg-secondary);
  border: 2px solid var(--pc-accent);
  border-radius: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 3;
  transition: box-shadow 0.15s ease;
  outline: none;
}

.rs-track__handle:hover,
.rs-track__handle:focus {
  box-shadow: 0 0 0 4px var(--pc-accent-glow);
}

/* ── Range labels ── */
.rs-filter__range-labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--pc-text-xs);
  color: var(--pc-text-muted);
  margin-bottom: 0.5rem;
}

/* ── Inputs ── */
.rs-filter__inputs {
  display: flex;
  gap: 0.5rem;
}

.rs-filter__input-group {
  flex: 1;
}

.rs-filter__label {
  display: block;
  font-size: var(--pc-text-xs);
  font-weight: 600;
  color: var(--pc-text-secondary);
  margin-bottom: 2px;
}

.rs-filter__input {
  width: 100%;
  padding: 0.3rem 0.5rem;
  font-size: var(--pc-text-sm);
  font-weight: 500;
  color: var(--pc-text-primary);
  background: var(--pc-bg-primary);
  border: 1px solid var(--pc-border);
  border-radius: var(--pc-radius-sm);
  outline: none;
  transition: border-color var(--pc-transition);
  -moz-appearance: textfield;
}

.rs-filter__input::-webkit-outer-spin-button,
.rs-filter__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.rs-filter__input:focus {
  border-color: var(--pc-accent);
  box-shadow: 0 0 0 2px var(--pc-accent-glow);
}

@media (max-width: 767px) {
  .rs-track {
    height: 2rem;
  }
  .rs-track__handle {
    width: 16px;
    height: 16px;
  }
}
</style>
